import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, ChevronRight, X } from "lucide-react";
import api from "../lib/api";

const KEY = "jatak_cookie_consent";

const CATEGORIES = [
  {
    id: "necessary",
    name: "Nødvendig",
    locked: true,
    desc: "Nødvendige cookies hjælper med at gøre hjemmesiden brugbar ved at aktivere grundlæggende funktioner såsom side-navigation og adgang til sikre områder af hjemmesiden. Hjemmesiden kan ikke fungere ordentligt uden disse cookies.",
  },
  {
    id: "preferences",
    name: "Præferencer",
    desc: "Præference cookies gør det muligt for en hjemmeside at huske oplysninger, der ændrer den måde hjemmesiden ser ud eller opfører sig på. F.eks. dit foretrukne sprog, eller den region, du befinder dig i.",
  },
  {
    id: "statistics",
    name: "Statistik",
    desc: "Statistiske cookies giver hjemmesideejere indsigt i brugernes interaktion med hjemmesiden, ved at indsamle og rapportere oplysninger anonymt.",
  },
  {
    id: "marketing",
    name: "Marketing",
    desc: "Marketing cookies bruges til at spore brugere på tværs af websites. Hensigten er at vise annoncer, der er relevante og engagerende for den enkelte bruger, og dermed mere værdifulde for udgivere og tredjeparts-annoncører.",
  },
];

function Toggle({ on, locked, onChange }) {
  return (
    <button
      type="button"
      className={"ck-toggle" + (on ? " on" : "") + (locked ? " locked" : "")}
      role="switch"
      aria-checked={on}
      disabled={locked}
      onClick={() => !locked && onChange(!on)}
      data-testid="cookie-toggle"
    >
      <span className="ck-knob" />
    </button>
  );
}

const CAT_DA = { necessary: "Nødvendig", preferences: "Præferencer", statistics: "Statistik", marketing: "Marketing" };

function StorageList({ items, testid }) {
  if (!items) return null;
  if (!items.length) return <p className="ck-items-empty" data-testid={testid}>Ingen i brug</p>;
  return (
    <ul className="ck-items" data-testid={testid}>
      {items.map((x) => (
        <li key={x.name}>
          <code>{x.name}</code>
          <span>{x.purpose}</span>
          <small>{x.provider} · {x.type} · {x.expiry}</small>
        </li>
      ))}
    </ul>
  );
}

export default function CookieBanner() {
  const [decided, setDecided] = useState(true);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("consent");
  const [expanded, setExpanded] = useState(null);
  const [storage, setStorage] = useState(null);
  useEffect(() => {
    if (!open || storage) return;
    api.get("/legal/privatliv")
      .then((r) => setStorage(Object.fromEntries((r.data.storage || []).map((g) => [g.category, g.items]))))
      .catch(() => {});
  }, [open, storage]);
  const [prefs, setPrefs] = useState({ necessary: true, preferences: false, statistics: false, marketing: false });

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(KEY) || "null");
    } catch {}
    if (stored && stored.prefs) {
      setPrefs({ ...stored.prefs, necessary: true });
      setDecided(true);
    } else {
      setDecided(false);
    }
  }, []);

  const reopen = useCallback(() => {
    setTab("consent");
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, [reopen]);

  const save = (next) => {
    const final = { ...next, necessary: true };
    try {
      localStorage.setItem(KEY, JSON.stringify({ prefs: final, ts: Date.now() }));
    } catch {}
    setPrefs(final);
    setDecided(true);
    setOpen(false);
    window.dispatchEvent(new Event("cookie-consent-changed"));
  };

  const acceptAll = () => save({ necessary: true, preferences: true, statistics: true, marketing: true });
  const onlyNecessary = () => save({ necessary: true, preferences: false, statistics: false, marketing: false });
  const allowSelected = () => save(prefs);

  // Nothing to render: already decided and modal closed
  if (decided && !open) return null;

  // Compact first-visit banner (no modal open yet)
  if (!decided && !open) {
    return (
      <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookiesamtykke" data-testid="cookie-banner">
        <div className="cookie-inner">
          <div className="cookie-ico" aria-hidden="true">
            <Cookie size={22} />
          </div>
          <div className="cookie-text">
            <b>Vi bruger cookies</b>
            <p>
              Vi bruger nødvendige cookies, så JaTak Pro fungerer, og valgfrie cookies til præferencer, statistik og
              marketing. Du bestemmer selv. Læs mere i vores{" "}
              <Link to="/privatliv" data-testid="cookie-privacy-link">privatlivspolitik</Link>.
            </p>
          </div>
          <div className="cookie-actions">
            <button className="btn small" onClick={() => { setTab("details"); setOpen(true); }} data-testid="cookie-customize-btn">
              Tilpas valg
            </button>
            <button className="btn small" onClick={onlyNecessary} data-testid="cookie-necessary-btn">
              Kun nødvendige
            </button>
            <button className="btn jatak small" onClick={acceptAll} data-testid="cookie-accept-btn">
              Accepter alle
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full consent manager modal
  return (
    <div className="ck-overlay" role="dialog" aria-modal="true" aria-label="Cookieindstillinger" data-testid="cookie-modal">
      <div className="ck-modal">
        <div className="ck-head">
          <div className="ck-brand">
            <span className="cookie-ico" aria-hidden="true"><Cookie size={20} /></span>
            <b>Cookieindstillinger</b>
          </div>
          {decided && (
            <button className="ck-close" onClick={() => setOpen(false)} aria-label="Luk" data-testid="cookie-close-btn">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="ck-tabs" role="tablist">
          {[
            ["consent", "Samtykke"],
            ["details", "Detaljer"],
            ["about", "Om"],
          ].map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              className={"ck-tab" + (tab === id ? " active" : "")}
              onClick={() => setTab(id)}
              data-testid={"cookie-tab-" + id}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ck-body">
          {tab === "consent" && (
            <div className="ck-pane">
              <p>
                Vi bruger cookies til at få hjemmesiden til at fungere, til at huske dine valg og til statistik og
                marketing. Nogle cookies er nødvendige, mens andre først sættes, når du giver dit samtykke. Du kan til
                enhver tid ændre eller trække dit samtykke tilbage via "Administrer cookies" i bunden af siden.
              </p>
              <p>
                Læs mere om, hvordan vi behandler dine oplysninger, i vores{" "}
                <Link to="/privatliv">privatlivspolitik</Link>.
              </p>
            </div>
          )}

          {tab === "details" && (
            <div className="ck-pane">
              {CATEGORIES.map((c) => (
                <div className="ck-cat" key={c.id}>
                  <div className="ck-cat-row">
                    <button
                      className="ck-cat-title"
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      aria-expanded={expanded === c.id}
                      data-testid={"cookie-cat-" + c.id}
                    >
                      <ChevronRight size={18} className={"ck-chev" + (expanded === c.id ? " open" : "")} />
                      <span>{c.name}</span>
                    </button>
                    <Toggle
                      on={!!prefs[c.id]}
                      locked={c.locked}
                      onChange={(v) => setPrefs((p) => ({ ...p, [c.id]: v }))}
                    />
                  </div>
                  {expanded === c.id && (
                    <>
                      <p className="ck-cat-desc">{c.desc}</p>
                      <StorageList items={storage?.[CAT_DA[c.id]]} testid={"cookie-items-" + c.id} />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "about" && (
            <div className="ck-pane">
              <p>
                Cookies er små tekstfiler, som websteder kan bruge til at gøre en brugers oplevelse mere effektiv. Loven
                siger, at vi kan gemme cookies på din enhed, hvis de er strengt nødvendige for driften af denne side. For
                alle andre typer cookies har vi brug for din tilladelse.
              </p>
              <p>
                Denne side bruger forskellige typer cookies. Nogle cookies sættes af tredjepartstjenester, der vises på
                vores sider. Du kan til enhver tid ændre eller trække dit samtykke tilbage fra erklæringen om cookies.
              </p>
              <p>
                Dette websted drives af <b>Adtimera ApS</b> (CVR 40551867). Har du spørgsmål, er du velkommen til at
                skrive til <a href="mailto:info@jatakpro.dk">info@jatakpro.dk</a>.
              </p>
            </div>
          )}
        </div>

        <div className="ck-foot">
          <button className="btn" onClick={onlyNecessary} data-testid="cookie-only-necessary-btn">
            Kun nødvendige
          </button>
          <button className="btn" onClick={allowSelected} data-testid="cookie-allow-selected-btn">
            Tillad valgte
          </button>
          <button className="btn jatak" onClick={acceptAll} data-testid="cookie-allow-all-btn">
            Tillad alle
          </button>
        </div>
      </div>
    </div>
  );
}
